import { getFullDomainValue } from 'utils/general'
import { REGEX_PASSWORD, REGEX_DOMAIN, TLDS } from '../../constants'
import {
  GATED_MODE_EMAIL_GATE,
  GATED_MODE_PASSWORD,
  GATED_MODE_EMAIL_WHITELIST,
  GATED_MODE_SSO,
  ACTION_CREATE,
  ACTION_UPDATE
} from './interface'

const applyGatedModeSettings = (siteSettings) => {
  if (!siteSettings?.gatedMode) return

  switch (siteSettings.gatedMode) {
    case GATED_MODE_EMAIL_GATE:
      siteSettings.enableEmailGate = true
      siteSettings.passwordProtected = false
      siteSettings.enableEmailWhitelist = false
      siteSettings.enableSSO = false
      break
    case GATED_MODE_PASSWORD:
      siteSettings.enableEmailGate = false
      siteSettings.passwordProtected = true
      siteSettings.enableEmailWhitelist = false
      siteSettings.enableSSO = false
      break
    case GATED_MODE_EMAIL_WHITELIST:
      siteSettings.enableEmailWhitelist = true
      siteSettings.enableEmailGate = false
      siteSettings.passwordProtected = false
      siteSettings.enableSSO = false
      break
    case GATED_MODE_SSO:
      siteSettings.enableSSO = true
      siteSettings.enableEmailGate = false
      siteSettings.passwordProtected = false
      siteSettings.enableEmailWhitelist = false
      break
    default:
      siteSettings.enableEmailGate = false
      siteSettings.passwordProtected = false
      siteSettings.enableEmailWhitelist = false
      siteSettings.enableSSO = false
  }
  delete siteSettings.gatedMode
}

export const validate = ({ domainValues, uploadValues, toggleValues }) => {
  const errors = {}

  const subdomain = domainValues.subdomain
  if (subdomain || (domainValues.isCustomDomain && !subdomain)) {
    const subdomainLower = subdomain?.toLowerCase()

    if (!REGEX_DOMAIN.test(subdomainLower)) {
      errors.domainValues = {
        ...errors.domainValues,
        subdomain: 'Invalid link-name: Only letters, numbers, and hyphens/dashes are allowed.'
      }
    } else if (subdomainLower === 'www' && Object.values(TLDS).includes(domainValues.domainSuffix)) {
      errors.domainValues = {
        ...errors.domainValues,
        subdomain: '"www" cannot be used as a subdomain for this domain.'
      }
    }
  }

  for (let uploadKey in uploadValues) {
    if (uploadKey === 'selectedFile' && !uploadValues[uploadKey]) {
      errors.uploadValues = { ...errors.uploadValues, selectedFile: 'Please select a file' }
    }
  }

  for (let toggleKey in toggleValues) {
    if (toggleKey === 'password' && toggleValues['passwordProtected']) {
      if (!REGEX_PASSWORD.test(toggleValues[toggleKey]?.toLowerCase())) {
        errors.toggleValues = { ...errors.toggleValues, password: 'Please enter a valid password' }
      }
    }

    if (toggleKey === 'gatedMode' && toggleValues[toggleKey] === GATED_MODE_EMAIL_WHITELIST) {
      if (!toggleValues['allowedEmails'] || toggleValues['allowedEmails'].length === 0) {
        errors.toggleValues = {
          ...errors.toggleValues,
          allowedEmails: 'Please add at least one email address'
        }
      }
    }
  }

  return errors
}

/** Care about changing this one, it get only form values changed by the user and create valid fields for request */
export const getUpdatedFields = (values, initialValues, returnUpdatedFields = false) => {
  let newValues = values
  let allCurrentFiles
  let allUpdatedFiles
  let filesToRemove
  let filesToAdd

  const updatedFields = Object.keys(newValues).reduce((acc, key) => {
    if (values[key] !== initialValues[key]) {
      if (key === 'subdomain' || key === 'domainSuffix') {
        acc['subdomain'] = values['subdomain']
        acc['domainSuffix'] = values['domainSuffix']
      } else if (key === 'disableExportPdf' || key === 'displayMode') {
        acc['disableExportPdf'] = values['disableExportPdf']
        acc['displayMode'] = values['displayMode']
      } else if (key === 'selectedFile' || key === 'addedFiles') {
        allCurrentFiles = initialValues.selectedFile ? [initialValues.selectedFile, ...initialValues.addedFiles] : []
        allUpdatedFiles = values.selectedFile ? [values.selectedFile, ...values.addedFiles] : []

        filesToRemove = allCurrentFiles.filter((currentFile) => {
          if (allUpdatedFiles.map((file) => file.name).includes(currentFile.name)) {
            if (allUpdatedFiles.find((file) => file.name === currentFile.name) instanceof File) return true
            return false
          } else return true
        })

        filesToAdd = allUpdatedFiles.filter((updatedFile) => {
          if (allCurrentFiles.map((file) => file.name).includes(updatedFile.name)) {
            if (updatedFile instanceof File) return true
            return false
          } else return true
        })

        acc['filesToAdd'] = filesToAdd
        acc['filesToRemove'] = filesToRemove

        if (values.indexFile) {
          acc['indexFile'] = values.indexFile
        }
      } else if (key === 'indexFile') {
        if (values.indexFile) {
          acc['indexFile'] = values.indexFile
        }
      } else {
        acc[key] = values[key]
      }
    }
    if (key === 'siteType') {
      acc['siteType'] = values['siteType']
    }

    return acc
  }, {})

  if (returnUpdatedFields) {
    if (Object.keys(updatedFields).length === 0) {
      return initialValues
    } else {
      return { ...initialValues, ...updatedFields }
    }
  }

  return Object.keys(updatedFields).length === 0 ? null : updatedFields
}

export const updateSiteRequest = ({ domainValuesToUpdate, toggleValuesToUpdate, uploadValuesToUpdate, siteType }) => {
  let newDomain = domainValuesToUpdate?.subdomain?.trim()
    ? getFullDomainValue(domainValuesToUpdate.subdomain, domainValuesToUpdate.domainSuffix)
    : null
  let siteSettings = toggleValuesToUpdate
  let filesToAdd = uploadValuesToUpdate?.filesToAdd
  let filesToRemove = uploadValuesToUpdate?.filesToRemove
  let indexFile = uploadValuesToUpdate?.indexFile
  let ogImageFile = null
  let logoImageFile = null

  if (siteSettings?.disableIndexing) {
    siteSettings.disableIndexing = siteSettings.disableIndexing.toString()
  }

  if (siteSettings?.siteConfiguration || siteSettings?.errorPath) {
    const config = {}
    if (siteSettings.errorPath) {
      config.errorPath = siteSettings.errorPath
    }
    siteSettings.siteConfiguration = JSON.stringify(config)
    delete siteSettings.errorPath
  }

  if (siteSettings?.ogImage && typeof siteSettings.ogImage !== 'string' && !(siteSettings.ogImage instanceof String)) {
    ogImageFile = siteSettings.ogImage
    delete siteSettings.ogImage
  }

  if (
    siteSettings?.logoImage &&
    typeof siteSettings.logoImage !== 'string' &&
    !(siteSettings.logoImage instanceof String)
  ) {
    logoImageFile = siteSettings.logoImage
    delete siteSettings.logoImage
  }

  applyGatedModeSettings(siteSettings)

  if (siteType) {
    return {
      newDomain,
      siteSettings,
      filesToAdd,
      filesToRemove,
      indexFile,
      siteType,
      ogImageFile,
      logoImageFile
    }
  }

  return {
    newDomain,
    siteSettings,
    filesToAdd,
    filesToRemove,
    indexFile,
    ogImageFile,
    logoImageFile
  }
}

export const createNewSiteRequest = ({ domainValues, toggleValues, uploadValues, siteType, spaceId }) => {
  let newDomain = domainValues?.subdomain
    ? getFullDomainValue(domainValues?.subdomain, domainValues?.domainSuffix)
    : null
  let domainSuffix = domainValues.domainSuffix
  let siteSettings = { ...toggleValues }
  let filesToAdd = uploadValues?.filesToAdd
  let indexFile = uploadValues?.indexFile
  let isZipPhpFile = uploadValues?.isZipPhpFile

  if (siteSettings?.disableIndexing) {
    siteSettings.disableIndexing = siteSettings.disableIndexing.toString()
  }

  if (siteSettings?.siteConfiguration && siteSettings?.errorPath) {
    siteSettings.siteConfiguration = JSON.stringify({ errorPath: siteSettings.errorPath })
    delete siteSettings.errorPath
  }

  applyGatedModeSettings(siteSettings)

  const request = {
    newDomain,
    domainSuffix,
    siteSettings,
    filesToAdd,
    indexFile,
    spaceId
    //timeZone: TIMEZONE // Disable approved regions
  }

  if (siteType) {
    return {
      ...request,
      siteType
    }
  }

  return {
    ...request,
    isZipPhpFile
  }
}

export const checkAccountSizeLimit = ({
  userProfile = {},
  sites = [],
  action = 'CREATE',
  currentSite = null,
  selectedFile = null,
  addedFiles = []
}) => {
  if (!userProfile?.accountLimit || userProfile.accountLimit === 0) {
    return {
      exceedsLimit: false,
      totalSize: 0,
      accountLimit: 0,
      hasLimit: false,
      availableSpace: Infinity
    }
  }

  const GB_TO_BYTES = 1024 * 1024 * 1024
  const accountLimit = userProfile?.accountLimit * GB_TO_BYTES

  const existingSitesTotalSize = sites.reduce((total, site) => {
    return total + (site.size || 0)
  }, 0)

  const allFiles = [selectedFile, ...addedFiles].filter(Boolean)
  const newFilesSize = allFiles.reduce((total, file) => {
    return total + (file.size || 0)
  }, 0)

  let projectedTotalSize = existingSitesTotalSize
  let sizeChange = 0

  if (action === ACTION_CREATE) {
    sizeChange = newFilesSize
    projectedTotalSize += newFilesSize
  } else if (action === ACTION_UPDATE && currentSite) {
    const currentSiteSize = currentSite?.size || 0
    sizeChange = newFilesSize - currentSiteSize
    projectedTotalSize = existingSitesTotalSize + sizeChange
  }

  const exceedsLimit = projectedTotalSize > accountLimit
  const availableSpace = Math.max(0, accountLimit - existingSitesTotalSize)

  return {
    exceedsLimit,
    totalSize: projectedTotalSize,
    accountLimit,
    existingSitesTotalSize,
    sizeChange,
    availableSpace,
    hasLimit: true,
    projectedUsagePercentage: (projectedTotalSize / accountLimit) * 100
  }
}
